/**
 * Componente ProtectedRoute
 * Protege rotas que requerem autenticação
 */

import React from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AppLayout from "../layout/AppLayout";
import AccessDeniedState from "./AccessDeniedState";
import { CRITICAL_SCREEN_PERMISSION_MAP } from "../../constants/permissionCatalog";

/**
 * Componente que protege rotas baseado em autenticação
 * @param {React.ReactNode} children - Componente filho a ser renderizado (opcional, se não fornecido usa Outlet)
 * @param {string} requiredRole - Role obrigatório (opcional)
 * @param {string} requiredPermission - Permissão granular obrigatória (opcional)
 * @param {string[]} requiredAnyPermissions - Lista de permissões (acesso se possuir ao menos uma)
 */
const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredPermission = null,
  requiredAnyPermissions = null,
}) => {
  const { isAuthenticated, user, loading, hasEffectivePermission, granularPermissionsEnabled } = useAuth();
  const location = useLocation();

  // Aguarda verificação de autenticação
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se não estiver autenticado, redireciona para login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se houver role obrigatório e o usuário não tiver, redireciona
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <AppLayout>
        <AccessDeniedState
          description="Seu perfil não possui acesso a esta funcionalidade."
        />
      </AppLayout>
    );
  }

  if (requiredPermission && granularPermissionsEnabled && !hasEffectivePermission(requiredPermission)) {
    return (
      <AppLayout>
        <AccessDeniedState
          description="Você não tem a permissão necessária para acessar esta funcionalidade."
        />
      </AppLayout>
    );
  }

  if (
    Array.isArray(requiredAnyPermissions) &&
    requiredAnyPermissions.length > 0 &&
    granularPermissionsEnabled &&
    !requiredAnyPermissions.some(permissionCode => hasEffectivePermission(permissionCode))
  ) {
    return (
      <AppLayout>
        <AccessDeniedState
          description="Você não tem a permissão necessária para acessar esta funcionalidade."
        />
      </AppLayout>
    );
  }

  // Quando não foi passado requisito explícito, aplica regra por rota crítica.
  const shouldApplyRouteDefaultCheck =
    !requiredPermission && (!Array.isArray(requiredAnyPermissions) || requiredAnyPermissions.length === 0);
  if (shouldApplyRouteDefaultCheck && granularPermissionsEnabled) {
    const routePermissions = CRITICAL_SCREEN_PERMISSION_MAP[location.pathname];
    if (
      Array.isArray(routePermissions) &&
      routePermissions.length > 0 &&
      !routePermissions.some(permissionCode => hasEffectivePermission(permissionCode))
    ) {
      return (
        <AppLayout>
          <AccessDeniedState
            description="Você não tem a permissão necessária para acessar esta funcionalidade."
          />
        </AppLayout>
      );
    }
  }

  // Se children for fornecido, renderiza com layout
  if (children) {
    return <AppLayout>{children}</AppLayout>;
  }

  // Se não, usa Outlet para renderizar rotas filhas
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
};

export default ProtectedRoute;
